import {
  DEMO_AI_FILLING_SUBSTEPS,
  DEMO_AI_FILLING_GROUPED_STEPS,
  DEMO_AI_FILLING_TOTAL_DURATION_MS,
  DEMO_AI_FILLING_QUESTION_AFTER_GROUP,
  TDemoAIFillingAgentStepDefinition,
  TDemoAIFillingSubstep,
} from '@commenda/types';
import { useInvalidateChatRooms } from '@commenda/api';
import { Button } from '@commenda/kakashi';
import { TaskStatus } from '@prisma/client';
import Lottie from 'lottie-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import {
  AnimatedGroupedTimeline,
  CollapsibleHistory,
  CompletedGroupedTimelineView,
} from './shared/TimelineComponents';

import commendaLoadingAnimation from '@commenda/kakashi/src/components/Loading/commenda-loading-animation.json';
import { useNotification } from '../../../../hooks/useNotification';

const STORAGE_KEY_PREFIX = 'ai-filling-start-';
const ANSWERS_KEY_PREFIX = 'ai-filling-answers-';
const TICK_INTERVAL_MS = 500;

function getStartStorageKey(taskId: number) {
  return `${STORAGE_KEY_PREFIX}${taskId}`;
}

function getAnswersStorageKey(taskId: number) {
  return `${ANSWERS_KEY_PREFIX}${taskId}`;
}

function loadAnswers(taskId: number): Record<number, string> {
  try {
    const raw = localStorage.getItem(getAnswersStorageKey(taskId));
    if (raw) return JSON.parse(raw) as Record<number, string>;
  } catch {
    // Corrupt data — ignore
  }
  return {};
}

function saveAnswer(taskId: number, stepIndex: number, answer: string) {
  const answers = loadAnswers(taskId);
  answers[stepIndex] = answer;
  localStorage.setItem(getAnswersStorageKey(taskId), JSON.stringify(answers));
}

/**
 * Walks through substeps, accumulating durationMs for text steps.
 * Question steps block if unanswered; answered questions are skipped (zero cost).
 * Returns the index of the current substep, or substeps.length if all done.
 */
function getCurrentSubstepIndex(
  substeps: TDemoAIFillingSubstep[],
  elapsedMs: number,
  answers: Record<number, string>
): number {
  let cumulative = 0;
  for (let i = 0; i < substeps.length; i += 1) {
    const step = substeps[i];
    if (step.type === 'question') {
      if (answers[i] === undefined) return i;
    } else {
      cumulative += step.durationMs;
      if (elapsedMs < cumulative) return i;
    }
  }
  return substeps.length;
}

/**
 * Returns the sum of durationMs for all text steps before (and not including)
 * the given stepIndex. Useful for resetting the start time after a question.
 */
function getTextDurationBefore(
  substeps: TDemoAIFillingSubstep[],
  beforeIndex: number
): number {
  let sum = 0;
  for (let i = 0; i < beforeIndex; i += 1) {
    const step = substeps[i];
    if (step.type === 'text') sum += step.durationMs;
  }
  return sum;
}

function QuestionStepView({
  step,
  onAnswer,
}: {
  step: TDemoAIFillingSubstep & { type: 'question' };
  onAnswer: (answer: string) => void;
}) {
  return (
    <div className="p-4 flex flex-col gap-3">
      <Lottie animationData={commendaLoadingAnimation} loop autoplay style={{ width: 96, height: 96, transform: 'scale(3)' }} />
      <div className="flex flex-col gap-1">
        <p className="txt-medium-medium text-text-primary font-mono">{step.title}</p>
        <p className="txt-small-regular text-text-secondary">
          {step.description}
        </p>
      </div>
      <div className="flex items-center gap-2 border-t pt-4">
        <Button size="md" variant="primary" onClick={() => onAnswer('yes')}>
          {step.yesText}
        </Button>
        <Button size="md" variant="secondary" onClick={() => onAnswer('no')}>
          {step.noText}
        </Button>
      </div>
    </div>
  );
}

export const DemoAIFillingAgentTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { setNotification } = useNotification();
  const { workflowExecution, serviceRequest, mutate } = context;
  const invalidateChatRooms = useInvalidateChatRooms();

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as TDemoAIFillingAgentStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const filingType = stepDefinition?.aiFillingAgent?.filingType;

  const substeps = useMemo(
    () => DEMO_AI_FILLING_SUBSTEPS[filingType] ?? [],
    [filingType]
  );
  const groupedSteps = useMemo(
    () => DEMO_AI_FILLING_GROUPED_STEPS[filingType] ?? [],
    [filingType]
  );
  const totalDurationMs = DEMO_AI_FILLING_TOTAL_DURATION_MS[filingType] ?? 0;
  const questionAfterGroup = DEMO_AI_FILLING_QUESTION_AFTER_GROUP[filingType] ?? 0;

  const preQuestionGroups = useMemo(
    () => groupedSteps.slice(0, questionAfterGroup + 1),
    [groupedSteps, questionAfterGroup]
  );
  const postQuestionGroups = useMemo(
    () => groupedSteps.slice(questionAfterGroup + 1),
    [groupedSteps, questionAfterGroup]
  );

  const isAlreadyCompleted =
    task.status === TaskStatus.COMPLETED;
  const isDisabled = task.status === TaskStatus.BACKLOG;

  const [questionAnswers, setQuestionAnswers] = useState<
    Record<number, string>
  >(() => loadAnswers(task.id));

  const [currentAIStepIndex, setCurrentAIStepIndex] = useState<number>(() => {
    if (isAlreadyCompleted) return substeps.length;

    const stored = localStorage.getItem(getStartStorageKey(task.id));
    if (stored) {
      const startTime = parseInt(stored, 10);
      const elapsed = Date.now() - startTime;
      return getCurrentSubstepIndex(substeps, elapsed, loadAnswers(task.id));
    }
    return 0;
  });

  const hasMarkedInProgress = useRef(false);
  const hasMarkedCompleted = useRef(isAlreadyCompleted);

  const markInProgress = useCallback(async () => {
    if (
      hasMarkedInProgress.current ||
      task.status === TaskStatus.IN_PROGRESS ||
      task.status === TaskStatus.COMPLETED
    )
      return;
    hasMarkedInProgress.current = true;
    await updateTaskStatus({
      task,
      mutate,
      status: TaskStatus.IN_PROGRESS,
    });
    invalidateChatRooms(serviceRequest?.ChatRoom?.id);
  }, [task, mutate, invalidateChatRooms, serviceRequest?.ChatRoom?.id]);

  const markCompleted = useCallback(async () => {
    if (hasMarkedCompleted.current) return;
    hasMarkedCompleted.current = true;
    localStorage.removeItem(getStartStorageKey(task.id));
    localStorage.removeItem(getAnswersStorageKey(task.id));
    const result = await updateTaskStatus({
      task,
      mutate,
      status: TaskStatus.COMPLETED,
    });
    if (!result) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to mark as completed',
      });
      return;
    }
    mutate();
    invalidateChatRooms(serviceRequest?.ChatRoom?.id);
    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Task marked as completed',
    });
  }, [task, mutate, setNotification, invalidateChatRooms, serviceRequest?.ChatRoom?.id]);

  const handleQuestionAnswer = useCallback(
    (stepIndex: number, answer: string) => {
      saveAnswer(task.id, stepIndex, answer);
      const updatedAnswers = { ...questionAnswers, [stepIndex]: answer };
      setQuestionAnswers(updatedAnswers);

      const textDurationBefore = getTextDurationBefore(
        substeps,
        stepIndex + 1
      );
      localStorage.setItem(
        getStartStorageKey(task.id),
        String(Date.now() - textDurationBefore)
      );
    },
    [task.id, substeps, questionAnswers]
  );

  useEffect(() => {
    if (isAlreadyCompleted || isDisabled) return undefined;

    let stored = localStorage.getItem(getStartStorageKey(task.id));
    if (!stored) {
      const now = Date.now();
      localStorage.setItem(getStartStorageKey(task.id), String(now));
      stored = String(now);
    }

    const interval = setInterval(() => {
      const startVal = localStorage.getItem(getStartStorageKey(task.id));
      if (!startVal) return;
      const startTime = parseInt(startVal, 10);
      const elapsed = Date.now() - startTime;
      const currentAnswers = loadAnswers(task.id);
      const idx = getCurrentSubstepIndex(substeps, elapsed, currentAnswers);
      setCurrentAIStepIndex(idx);

      if (idx >= 1 && !hasMarkedInProgress.current) {
        markInProgress();
      }

      const currentStep = substeps[idx];
      const isBlockedOnQuestion =
        currentStep?.type === 'question' && currentAnswers[idx] === undefined;

      if (!isBlockedOnQuestion && elapsed >= totalDurationMs) {
        clearInterval(interval);
        setCurrentAIStepIndex(substeps.length);
        markCompleted();
      }
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [
    isAlreadyCompleted,
    isDisabled,
    substeps,
    totalDurationMs,
    task.id,
    markInProgress,
    markCompleted,
    questionAnswers,
  ]);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: () => ({
      actions: task.status === TaskStatus.BACKLOG ? <Button size="sm" variant="primary" disabled>
        File with AI
      </Button> : null,
      label: (
        <ServiceRequestTaskLabel
          label={
            task.status === TaskStatus.COMPLETED
              ? `${task.title} completed`
              : task.title
          }
          status={task.status}
          timestamp={
            task.status === TaskStatus.COMPLETED
              ? task.updatedAt
              : undefined
          }
        />
      ),
    }),
  };

  const isRunning =
    !isDisabled && !isAlreadyCompleted && currentAIStepIndex < substeps.length;
  const currentStep = substeps[currentAIStepIndex];
  const isBlockedOnQuestion =
    currentStep?.type === 'question' &&
    questionAnswers[currentAIStepIndex] === undefined;
  const hasAnsweredQuestion = Object.keys(questionAnswers).length > 0;

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role: context.role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {isRunning && (
        <div className="border border-fg-brand rounded-md mt-4 p-4 bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
          {isBlockedOnQuestion ? (
            <>
              <CompletedGroupedTimelineView steps={preQuestionGroups} />
              <QuestionStepView
                step={currentStep}
                onAnswer={(answer) =>
                  handleQuestionAnswer(currentAIStepIndex, answer)
                }
              />
            </>
          ) : hasAnsweredQuestion ? (
            <AnimatedGroupedTimeline
              steps={postQuestionGroups}
              initialCompletedGroups={preQuestionGroups}
            />
          ) : (
            <AnimatedGroupedTimeline steps={preQuestionGroups} />
          )}
        </div>
      )}
      {isAlreadyCompleted && groupedSteps.length > 0 && (
        <div className="border border-border-secondary rounded-md mt-4 px-6 py-1 bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
          <CollapsibleHistory>
            <CompletedGroupedTimelineView steps={groupedSteps} />
          </CollapsibleHistory>
        </div>
      )}
    </div>
  );
};
